<template>
  <Card class="w-full">
    <CardHeader class="flex flex-col gap-2">
      <div class="flex items-start justify-between gap-3">
        <div class="flex flex-col gap-1">
          <CardTitle class="text-base">
            <Badge :variant="isCompleted ? 'default' : 'secondary'">
              {{ checklistStatusLabel }}
            </Badge>
            <Badge v-if="completionPercentageLabel" variant="outline">
              {{ completionPercentageLabel }}
            </Badge>
            {{ checklistTitle }}
          </CardTitle>
        </div>
        <div class="flex items-center gap-1">
          <DropdownMenu v-if="showManageActions && !readonly">
            <DropdownMenuTrigger as-child>
              <Button aria-label="Checklist actions" size="icon-sm" variant="ghost" :disabled="isDeleting">
                <MoreHorizontal class="rotate-90" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-36">
              <DropdownMenuItem :disabled="isDeleting" @click="emit('edit', checklist)"> Edit </DropdownMenuItem>
              <DropdownMenuItem :disabled="isDeleting" class="text-destructive" @click="emit('delete', checklist)">
                {{ isDeleting ? 'Deleting...' : 'Delete' }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon-sm" class="size-8" @click="toggleMinimized">
            <ChevronDown v-if="isMinimized" class="h-4 w-4" />
            <ChevronUp v-else class="h-4 w-4" />
            <span class="sr-only">{{ isMinimized ? 'Expand checklist' : 'Minimize checklist' }}</span>
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent v-if="!isMinimized" class="flex flex-col gap-4">
      <div v-if="checklistTemplateEditRoute" class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <RouterLink :to="checklistTemplateEditRoute" class="transition hover:text-foreground"> From Template </RouterLink>
      </div>
      <CardDescription v-if="checklistTimingLabel">
        {{ checklistTimingLabel }}
      </CardDescription>
      <p v-if="checklistDescription" class="text-sm text-muted-foreground">
        {{ checklistDescription }}
      </p>
      <ChecklistItems :checklist-id="checklist.id" :is-not-executable="isNotExecutable" :readonly="readonly" />

      <div v-if="!readonly" class="flex flex-col gap-2 border-t pt-4">
        <Button variant="link" @click="toggleQuickAddVisibility">
          {{ quickAddToggleLabel }}
        </Button>

        <ChecklistItemAdd v-if="isQuickAddVisible" ref="quickAddForm" :checklist-id="checklist.id" :focus-signal="focusQuickAddSignal" @added="handleChecklistItemAdded" />
      </div>
    </CardContent>
  </Card>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge/index.js'
import { Button } from '@/components/ui/button/index.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu/index.js'
import ChecklistItemAdd from './ChecklistItemAdd.vue'
import ChecklistItems from './ChecklistItems.vue'
import { useChecklistItemsStore } from '../../stores/checklistItemsStore.js'

defineOptions({
  name: 'ChecklistCardItem',
})

const props = defineProps({
  checklist: {
    type: Object,
    required: true,
  },
  initiallyMinimized: {
    type: Boolean,
    default: false,
  },
  focusQuickAddSignal: {
    type: Number,
    default: 0,
  },
  showManageActions: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  isNotExecutable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['edit', 'delete'])

const route = useRoute()
const router = useRouter()
const checklistItemsStore = useChecklistItemsStore(`checklist_${props.checklist.id}`)
const isQuickAddVisible = ref(false)
const isMinimized = ref(props.initiallyMinimized)
const quickAddForm = ref(null)
const checklistItems = computed(() => checklistItemsStore.itemsForChecklist(props.checklist.id))
const hasLoadedChecklistItems = computed(() => {
  return checklistItemsStore.initializedChecklistIds.includes(Number(props.checklist.id))
})
const hasAllChecklistItemsCompleted = computed(() => {
  if (!hasLoadedChecklistItems.value || checklistItems.value.length < 1) {
    return false
  }

  return checklistItems.value.every((checklistItem) => checklistItem.completed === true)
})

const isCompleted = computed(() => {
  if (hasLoadedChecklistItems.value) {
    return hasAllChecklistItemsCompleted.value
  }

  return Boolean(props.checklist.completed_at)
})

const checklistTitle = computed(() => {
  const name = String(props.checklist.name ?? '').trim()

  return name.length > 0 ? name : `Checklist #${props.checklist.id}`
})

const checklistDescription = computed(() => {
  const description = String(props.checklist.description ?? '')

  return description.trim().length > 0 ? description : null
})

const completionPercentage = computed(() => {
  if (!hasLoadedChecklistItems.value || checklistItems.value.length < 1) {
    return null
  }

  const completedChecklistItemsCount = checklistItems.value.filter((checklistItem) => checklistItem.completed === true).length
  const percentage = Math.round((completedChecklistItemsCount / checklistItems.value.length) * 100)

  if (percentage < 1 || percentage > 99) {
    return null
  }

  return percentage
})

const completionPercentageLabel = computed(() => {
  return completionPercentage.value === null ? null : `${completionPercentage.value}%`
})

const checklistTemplateEditRoute = computed(() => {
  const checklistTemplateId = Number(props.checklist.checklist_template_id ?? 0)
  const businessSystemId = Number(route.params.businessSystem ?? 0)

  if (!Number.isFinite(checklistTemplateId) || checklistTemplateId < 1) {
    return null
  }

  if (businessSystemId > 0 && router.hasRoute('BdsChecklistTemplateShowPage')) {
    return {
      name: 'BdsChecklistTemplateShowPage',
      params: {
        businessSystem: businessSystemId,
        checklistTemplateId,
      },
    }
  }

  if (!router.hasRoute('ChecklistTemplateShowPage')) {
    return null
  }

  return {
    name: 'ChecklistTemplateShowPage',
    params: {
      checklistTemplateId,
    },
  }
})

const checklistStatusLabel = computed(() => {
  return isCompleted.value ? 'Completed' : 'In Progress'
})

const startedAtLabel = computed(() => {
  if (!props.checklist.started_at) {
    return null
  }

  const startedAtDate = new Date(props.checklist.started_at)

  if (Number.isNaN(startedAtDate.getTime())) {
    return null
  }

  return startedAtDate.toLocaleString()
})

const completedAtLabel = computed(() => {
  if (hasLoadedChecklistItems.value) {
    if (!hasAllChecklistItemsCompleted.value) {
      return null
    }

    const completedAtTimestamps = checklistItems.value.map((checklistItem) => checklistItem.completed_at).filter((completedAt) => typeof completedAt === 'string' && completedAt.trim().length > 0)

    if (completedAtTimestamps.length < 1) {
      return new Date().toLocaleString()
    }

    const latestCompletedAtDate = completedAtTimestamps
      .map((completedAt) => new Date(completedAt))
      .filter((completedAtDate) => !Number.isNaN(completedAtDate.getTime()))
      .sort((left, right) => right.getTime() - left.getTime())[0]

    if (!latestCompletedAtDate) {
      return new Date().toLocaleString()
    }

    return latestCompletedAtDate.toLocaleString()
  }

  if (!props.checklist.completed_at) {
    return null
  }

  const completedAtDate = new Date(props.checklist.completed_at)

  if (Number.isNaN(completedAtDate.getTime())) {
    return null
  }

  return completedAtDate.toLocaleString()
})

const checklistTimingLabel = computed(() => {
  const timingParts = [startedAtLabel.value ? `Started ${startedAtLabel.value}` : null, completedAtLabel.value ? `Completed ${completedAtLabel.value}` : null].filter((part) => part !== null)

  return timingParts.length > 0 ? timingParts.join(' • ') : null
})

const quickAddToggleLabel = computed(() => {
  return isQuickAddVisible.value ? 'Hide quick add checklist item' : 'Quick add checklist item'
})

const toggleMinimized = () => {
  isMinimized.value = !isMinimized.value
}

const toggleQuickAddVisibility = () => {
  if (props.readonly) {
    return
  }

  isQuickAddVisible.value = !isQuickAddVisible.value
}

const handleChecklistItemAdded = () => {}

watch(
  () => props.initiallyMinimized,
  (value) => {
    isMinimized.value = value
  },
)

watch(
  () => props.focusQuickAddSignal,
  async (signal) => {
    if (props.readonly || Number(signal) < 1) {
      return
    }

    isMinimized.value = false
    isQuickAddVisible.value = true
    await nextTick()
    await quickAddForm.value?.focusInput?.()
  },
  { immediate: true },
)
</script>
